'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactNode } from 'react';
import { InterfaceChainUtils, ZERO } from '@morpho-org/dapp-core';
import { Avatar, AvatarMultichain, HoverableCell } from '@morpho-org/ui-v6/client';
import { CellAllocation, V1Badge } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Icon, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui';
import { ArrowUpToLine, PointsGradient, RewardsGradient, WarningTriangleSolid } from '@repo/ui/icons';
import { InternalLink } from '@/components/common/InternalLink';
import RewardsHoverCard from '@/components/common/RewardsHoverCard';
import MerklRewardsHoverCard from '@/components/common/RewardsHoverCard/MerklRewardsHoverCard';
import { VaultV2MaxRateWarning } from '@/components/common/VaultV2MaxRateWarning';
import { mapVaultRowApyCardRows } from '@/helpers/vaultApyCardRows';
import type { CardNetApyVariant, NetApyHoverCardProps, RewardTokenItem } from '../CardNetApy';
interface CardNetApyHoverProps {
  isVaultV2?: boolean;
  maxRate?: bigint | null;
  hoverCardProps: NetApyHoverCardProps;
  variant: CardNetApyVariant;
  children: ReactNode;
}
const ApyHoverCardContent = t0 => {
  const $ = _c(24);
  const {
    isVaultV2,
    hoverCardProps,
    variant,
    maxRate
  } = t0;
  if (isVaultV2) {
    const t1 = hoverCardProps.apyBreakdown ?? null;
    let t2;
    if ($[0] !== hoverCardProps.asset || $[1] !== hoverCardProps.includesDonations || $[2] !== hoverCardProps.isRateCapped || $[3] !== hoverCardProps.managementFee || $[4] !== hoverCardProps.maxApy || $[5] !== hoverCardProps.nativeApy || $[6] !== hoverCardProps.netApy || $[7] !== hoverCardProps.performanceFee || $[8] !== hoverCardProps.points || $[9] !== hoverCardProps.rewards || $[10] !== maxRate || $[11] !== t1 || $[12] !== variant) {
      t2 = <MerklRewardsHoverCard apy={hoverCardProps.nativeApy} netApy={hoverCardProps.netApy} performanceFee={hoverCardProps.performanceFee} managementFee={hoverCardProps.managementFee} rewards={hoverCardProps.rewards} asset={hoverCardProps.asset} points={hoverCardProps.points} variant={variant} maxRate={maxRate} maxApy={hoverCardProps.maxApy} isRateCapped={hoverCardProps.isRateCapped} includesDonations={hoverCardProps.includesDonations} apyBreakdown={t1} />;
      $[0] = hoverCardProps.asset;
      $[1] = hoverCardProps.includesDonations;
      $[2] = hoverCardProps.isRateCapped;
      $[3] = hoverCardProps.managementFee;
      $[4] = hoverCardProps.maxApy;
      $[5] = hoverCardProps.nativeApy;
      $[6] = hoverCardProps.netApy;
      $[7] = hoverCardProps.performanceFee;
      $[8] = hoverCardProps.points;
      $[9] = hoverCardProps.rewards;
      $[10] = maxRate;
      $[11] = t1;
      $[12] = variant;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  let t1;
  if ($[14] !== hoverCardProps.asset || $[15] !== hoverCardProps.managementFee || $[16] !== hoverCardProps.nativeApy || $[17] !== hoverCardProps.netApy || $[18] !== hoverCardProps.performanceFee || $[19] !== hoverCardProps.points || $[20] !== hoverCardProps.rewards || $[21] !== maxRate || $[22] !== variant) {
    t1 = <RewardsHoverCard apy={hoverCardProps.nativeApy} netApy={hoverCardProps.netApy} performanceFee={hoverCardProps.performanceFee} managementFee={hoverCardProps.managementFee} rewards={hoverCardProps.rewards} asset={hoverCardProps.asset} points={hoverCardProps.points} variant={variant} maxRate={maxRate} />;
    $[14] = hoverCardProps.asset;
    $[15] = hoverCardProps.managementFee;
    $[16] = hoverCardProps.nativeApy;
    $[17] = hoverCardProps.netApy;
    $[18] = hoverCardProps.performanceFee;
    $[19] = hoverCardProps.points;
    $[20] = hoverCardProps.rewards;
    $[21] = maxRate;
    $[22] = variant;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  return t1;
};
interface ApyHoverIndicatorsProps {
  children: ReactNode;
  hasRewardSparkle: boolean;
  isPaused: boolean;
  hasCapIcon: boolean;
  hasPoints: boolean;
}
const ApyHoverIndicators = t0 => {
  const $ = _c(12);
  const {
    children,
    hasRewardSparkle,
    isPaused,
    hasCapIcon,
    hasPoints
  } = t0;
  let t1;
  if ($[0] !== hasRewardSparkle) {
    t1 = hasRewardSparkle ? <Icon icon={<RewardsGradient data-testid="card-net-apy-reward-icon" />} /> : null;
    $[0] = hasRewardSparkle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasCapIcon || $[3] !== isPaused) {
    t2 = isPaused ? <Icon icon={<WarningTriangleSolid data-testid="card-net-apy-paused-icon" />} /> : hasCapIcon ? <Icon icon={<ArrowUpToLine data-testid="card-net-apy-cap-icon" />} /> : null;
    $[2] = hasCapIcon;
    $[3] = isPaused;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== hasPoints) {
    t3 = hasPoints ? <Icon icon={<PointsGradient data-testid="card-net-apy-points-icon" />} /> : null;
    $[5] = hasPoints;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex items-center gap-[2px]">{children}{t1}{t2}{t3}</div>;
    $[7] = children;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
const CardNetApyHover = t0 => {
  const $ = _c(24);
  const {
    isVaultV2,
    maxRate,
    hoverCardProps,
    variant,
    children
  } = t0;
  let t1;
  if ($[0] !== hoverCardProps.apyBreakdown || $[1] !== hoverCardProps.includesDonations || $[2] !== hoverCardProps.isRateCapped || $[3] !== hoverCardProps.maxApy || $[4] !== hoverCardProps.nativeApy || $[5] !== hoverCardProps.netApy || $[6] !== hoverCardProps.rewards || $[7] !== isVaultV2) {
    t1 = isVaultV2 ? mapVaultRowApyCardRows({
      breakdown: hoverCardProps.apyBreakdown ?? null,
      apy: hoverCardProps.nativeApy,
      netApy: hoverCardProps.netApy,
      rewards: hoverCardProps.rewards,
      includesDonations: hoverCardProps.includesDonations,
      isRateCapped: hoverCardProps.isRateCapped,
      maxApy: hoverCardProps.maxApy
    }) : null;
    $[0] = hoverCardProps.apyBreakdown;
    $[1] = hoverCardProps.includesDonations;
    $[2] = hoverCardProps.isRateCapped;
    $[3] = hoverCardProps.maxApy;
    $[4] = hoverCardProps.nativeApy;
    $[5] = hoverCardProps.netApy;
    $[6] = hoverCardProps.rewards;
    $[7] = isVaultV2;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const rows = t1;
  const hasPoints = !!hoverCardProps.points?.length;
  const hasV2Card = (rows?.hasCard ?? false) || hasPoints;
  const t2 = !!isVaultV2 && !hasV2Card;
  let t3;
  if ($[9] !== hoverCardProps || $[10] !== isVaultV2 || $[11] !== maxRate || $[12] !== variant) {
    t3 = <ApyHoverCardContent isVaultV2={isVaultV2} hoverCardProps={hoverCardProps} variant={variant} maxRate={maxRate} />;
    $[9] = hoverCardProps;
    $[10] = isVaultV2;
    $[11] = maxRate;
    $[12] = variant;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = rows?.hasSparkle ?? false;
  const t5 = rows?.hasPausedIcon ?? false;
  const t6 = rows?.hasCapIcon ?? false;
  const t7 = !!isVaultV2 && hasPoints;
  let t8;
  if ($[14] !== children || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <ApyHoverIndicators hasRewardSparkle={t4} isPaused={t5} hasCapIcon={t6} hasPoints={t7}>{children}</ApyHoverIndicators>;
    $[14] = children;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t2 || $[21] !== t3 || $[22] !== t8) {
    t9 = <HoverableCell withBackgroundEffect={false} withTouch={true} isDisabled={t2} isKeyboardTrigger={true} hoverCardContent={t3}>{t8}</HoverableCell>;
    $[20] = t2;
    $[21] = t3;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
export type CardNetApyRowItemProps = RewardTokenItem & {
  index: number;
  symbol?: string;
  hideAllocation?: boolean;
  chainId?: ChainId;
  maxRate?: bigint | null;
  href?: string;
  hoverCardProps?: NetApyHoverCardProps;
  variant?: CardNetApyVariant;
  tooltipContent?: ({
    allocation,
    assetsUsd,
    symbol
  }: {
    allocation: bigint;
    assetsUsd: bigint;
    symbol?: string;
  }) => string;
};
const percentageFormatter = formatters.percent.trailingZero(false).digits(2).max(10).unit('%');
const usdFormatter = formatters.short.trailingZero(false).digits(2).unit('$');
const CardNetApyRowItem = t0 => {
  const $ = _c(59);
  const {
    name,
    chainId,
    market,
    logoURIs,
    weight,
    allocation,
    apr,
    index,
    percentageOffset,
    symbol,
    hideAllocation,
    tooltipContent,
    isVaultV2,
    maxRate,
    href,
    hoverCardProps,
    variant
  } = t0;
  const withAvatarPairs = logoURIs && logoURIs.length > 1;
  let t1;
  if ($[0] !== chainId) {
    t1 = chainId ? {
      id: chainId,
      name: InterfaceChainUtils.getMetadata(chainId).chain.name
    } : null;
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const chain = t1;
  let t2;
  if ($[2] !== chain || $[3] !== href || $[4] !== logoURIs || $[5] !== market || $[6] !== name || $[7] !== withAvatarPairs) {
    t2 = market ? <span className="gap-2xs inline-flex min-w-0 items-center"><Avatar aria-hidden={true} src={market.loanLogoURI ?? null} size="small" /><TruncatedTooltip text={market.loanSymbol ?? ""}>{(symbolRef, isTruncated) => <span ref={symbolRef} tabIndex={isTruncated && href ? -1 : undefined} className="text-3xs text-secondary-foreground min-w-0 truncate">{market.loanSymbol}</span>}</TruncatedTooltip><span aria-hidden={true} className="bg-muted-foreground h-sm w-px shrink-0 rounded-full" /><span className="sr-only">backed by</span><span className="gap-2xs inline-flex min-w-0 items-center"><AvatarMultichain src={market.collateralLogoURI ?? null} chain={chain} size="small" networkSize={12} networkOffset={{
          right: -2,
          bottom: 0
        }} /><TruncatedTooltip text={market.collateralSymbol ?? ""}>{(symbolRef_0, isTruncated_0) => <span ref={symbolRef_0} tabIndex={isTruncated_0 && href ? -1 : undefined} className="text-3xs text-secondary-foreground min-w-0 truncate">{market.collateralSymbol}</span>}</TruncatedTooltip></span></span> : <>{withAvatarPairs ? <div className="inline-flex items-center"><Avatar src={logoURIs[0] ?? null} size="small" /><AvatarMultichain src={logoURIs[1] ?? null} chain={chain} size="small" networkSize={12} networkOffset={{
          right: -2,
          bottom: 0
        }} className="-ml-sm" /></div> : logoURIs?.length === 1 ? <AvatarMultichain src={logoURIs[0] ?? null} chain={chain} size="small" /> : null}<span className="text-3xs text-secondary-foreground max-w-[130px] min-w-0 truncate text-start">{name}</span></>;
    $[2] = chain;
    $[3] = href;
    $[4] = logoURIs;
    $[5] = market;
    $[6] = name;
    $[7] = withAvatarPairs;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== href || $[10] !== isVaultV2) {
    t3 = isVaultV2 === false && <V1Badge tabIndex={href ? -1 : 0} />;
    $[9] = href;
    $[10] = isVaultV2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== isVaultV2 || $[13] !== maxRate) {
    t4 = isVaultV2 && maxRate === ZERO && <VaultV2MaxRateWarning />;
    $[12] = isVaultV2;
    $[13] = maxRate;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <div className="gap-2xs flex min-w-0 items-center overflow-hidden">{t2}{t3}{t4}</div>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const nameContent = t5;
  let t6;
  if ($[19] !== apr) {
    t6 = percentageFormatter.of(apr, 18);
    $[19] = apr;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const formattedApr = t6;
  let t7;
  if ($[21] !== formattedApr) {
    t7 = <span className="text-3xs text-secondary-foreground text-right">{formattedApr}</span>;
    $[21] = formattedApr;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const apyContent = t7;
  let t8;
  if ($[23] !== href || $[24] !== nameContent) {
    t8 = href ? <InternalLink href={href} withHoverEffect={true} className="px-2xs min-w-0 shrink overflow-hidden py-[2px]">{nameContent}</InternalLink> : nameContent;
    $[23] = href;
    $[24] = nameContent;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = `gap-2xs flex shrink-0 items-center ${hideAllocation ? "justify-end" : "justify-between"}`;
  let t10;
  if ($[26] !== allocation || $[27] !== hideAllocation || $[28] !== index || $[29] !== percentageOffset) {
    t10 = !hideAllocation && <CellAllocation hideLabel={true} index={index} allocation={allocation} percentageOffset={percentageOffset} />;
    $[26] = allocation;
    $[27] = hideAllocation;
    $[28] = index;
    $[29] = percentageOffset;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== weight) {
    t11 = usdFormatter.of(weight, 18);
    $[31] = weight;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11) {
    t12 = <TooltipTrigger asChild={true}><span className="text-3xs text-secondary-foreground text-right">{t11}</span></TooltipTrigger>;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== allocation || $[36] !== symbol || $[37] !== tooltipContent || $[38] !== weight) {
    t13 = tooltipContent?.({
      allocation,
      assetsUsd: weight,
      symbol
    });
    $[35] = allocation;
    $[36] = symbol;
    $[37] = tooltipContent;
    $[38] = weight;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13) {
    t14 = <TooltipContent>{t13}</TooltipContent>;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t14) {
    t15 = <Tooltip>{t12}{t14}</Tooltip>;
    $[42] = t12;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== apyContent || $[46] !== hoverCardProps || $[47] !== isVaultV2 || $[48] !== maxRate || $[49] !== variant) {
    t16 = hoverCardProps && variant ? <CardNetApyHover isVaultV2={isVaultV2} maxRate={maxRate} hoverCardProps={hoverCardProps} variant={variant}>{apyContent}</CardNetApyHover> : apyContent;
    $[45] = apyContent;
    $[46] = hoverCardProps;
    $[47] = isVaultV2;
    $[48] = maxRate;
    $[49] = variant;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t10 || $[52] !== t15 || $[53] !== t16 || $[54] !== t9) {
    t17 = <div className={t9}>{t10}{t15}{t16}</div>;
    $[51] = t10;
    $[52] = t15;
    $[53] = t16;
    $[54] = t9;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t17 || $[57] !== t8) {
    t18 = <div className="px-md flex h-10 items-center justify-between">{t8}{t17}</div>;
    $[56] = t17;
    $[57] = t8;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  return t18;
};
export default CardNetApyRowItem;